import { useMessageSource } from "react-message-source";
import * as Yup from "yup";
import useProductValidator from "../../ui/widgets/product-selection/validator";
import { useAddressValidator } from "../shared/components/address-select/validator";
import { useTextInputValidator } from "../shared/components/formik/text-input/validator";
import usePhoneNumberValidator from "../shared/components/phone/validator";
import {
  useMandatoryStringTypeValidator,
  useMandatoryTypeValidator,
  usePartialDateValidator,
} from "../shared/components/validator";

const MIN_COMPANY_NAME_LENGTH = 2;
const MAX_COMPANY_NAME_LENGTH = 255;

export default function useHomeValidationSchema() {
  const { getMessage } = useMessageSource();

  const cybwellCompanyDataValidator = () => {
    return Yup.mixed()
      .required(getMessage("Validation.message-required"))
      .test(
        "isCybwellDataComplete",
        getMessage("Validation.message-cybwellDataNotComplete"),
        (company) => {
          const companySchema = Yup.object().shape({
            name: Yup.string().required(),
            address: Yup.object()
              .required()
              .shape({
                street: Yup.string().required(),
                zipCode: Yup.string().required(),
                city: Yup.mixed().required(),
                country: Yup.object()
                  .required()
                  .shape({ alpha2: Yup.string().required() }),
              }),
            legalForm: Yup.string().required(),
          });
          return companySchema.isValid(company);
        },
      );
  };

  const companyNameValidator = () =>
    Yup.string()
      .nullable()
      .min(
        MIN_COMPANY_NAME_LENGTH,
        getMessage(
          "Validation.message-minLengthCharacters",
          MIN_COMPANY_NAME_LENGTH,
        ),
      )
      .max(
        MAX_COMPANY_NAME_LENGTH,
        getMessage(
          "Validation.message-maxLengthCharacters",
          MAX_COMPANY_NAME_LENGTH,
        ),
      )
      .required(getMessage("Validation.message-required"));

  const mandatoryStringValidator = useMandatoryStringTypeValidator();

  const companyValidator = Yup.object().shape({
    name: companyNameValidator(),
    fullStreet: useTextInputValidator(),
    address: useAddressValidator(),
    phone: usePhoneNumberValidator(),
    legalForm: useMandatoryTypeValidator(),
    industryBranchCode: mandatoryStringValidator,
    industrySubBranchCode: Yup.string().when("industryBranchCode", {
      is: (industryBranchCodeValue: string) => {
        return Boolean(industryBranchCodeValue?.startsWith("fake"));
      },
      then: () => mandatoryStringValidator,
    }),
    foundationDate: usePartialDateValidator().required(
      getMessage("Validation.message-required"),
    ),
  });

  return Yup.object().shape({
    selectedProduct: useProductValidator(),
    selectedCompany: Yup.mixed()
      .nullable()
      .when("isCompanyManuallyEntered", {
        is: false,
        then: () => cybwellCompanyDataValidator(),
      }),
    manuallyEnteredCompany: Yup.object().when("isCompanyManuallyEntered", {
      is: true,
      then: () => companyValidator,
    }),
  });
}
